(ns frontend.view.nev.kyc.process.screening-profile-table
  (:require
   [clojure.string :as string]
   [common.country :as country]
   [common.localization :as loc]
   [common.schema.entities.workspace.kyc.templates.templates :as kyc-templates]
   [common.schema.entities.screening-profile.materialization :as sp-materialization]
   [common.util :as cu]
   [frontend.actions.screening-profile :as sp-actions]
   [frontend.db :as app-db]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.grid.screening.events :as sp-events]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.notification.view :as notification-modal]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]))

(defn get-entity-risk-screen-url
  [external-screening-id]
  (str "https://batch.riskscreen.com/entity/view/" external-screening-id "?pageindex=0&rowsperpage=100"))

(defn is-missing-data?
  [persons]
  (->> persons
       (filter (fn [{:keys [first-name last-name]}]
                 (not (and first-name last-name))))
       (count)
       (< 0)))

(defn get-containing-kyc-profile
  [kyc-process-id]
  (let [kyc-profiles (concat
                      (session/get-entities :entity.type/kyc-profile)
                      (session/get-entities :entity.type/tenant-kyc-profile)
                      (session/get-entities :entity.type/contract-kyc-profile))
        containing-profile (->> kyc-profiles
                                (filter (fn [kyc-profile]
                                          (let [kyc-processes (or (:kyc-profile/kyc-processes kyc-profile)
                                                                  (:tenant-kyc-profile/kyc-processes kyc-profile)
                                                                  (:contract-kyc-profile/kyc-processes kyc-profile))]
                                            (some #{kyc-process-id} kyc-processes))))
                                (first))]
    containing-profile))

(defn deduplicate-key-fn
  [values]
  (->> values
       (map cu/str-trim-lcase)
       (string/join "-")
       (cu/str-trim-lcase)))

(defn find-matching-screening-profile
  [type person-or-legal-entity screening-profiles]
  (case type
    :person
    (let [{:keys [first-name last-name date-of-birth country]} person-or-legal-entity
          matching-profiles (->> screening-profiles
                                 (filter (fn [screening-profile]
                                           (= (deduplicate-key-fn [first-name last-name date-of-birth country])
                                              (deduplicate-key-fn [(:screening-profile/first-name screening-profile)
                                                                   (:screening-profile/last-name screening-profile)
                                                                   (:screening-profile/date-of-birth screening-profile)
                                                                   (:screening-profile/country screening-profile)])))))]
      (or (->> matching-profiles
               (cu/filter-by :screening-profile/screening-status :screening-status/active)
               (first))
          (first matching-profiles)))
    :company
    (->> screening-profiles
         (filter (fn [{:keys [screening-profile/type
                              screening-profile/company-name]}]
                   (and (= type :screening-profile-type/company)
                        (= company-name (:company-name person-or-legal-entity)))))
         (first))
    nil))

(rf/reg-sub
 :screening-profiles
 :<-
 [:entity-types]
 (fn [grouped-entities _]
   (:entity.type/screening-profile grouped-entities)))

(defn get-sanction-str
  [sanction-status]
  (case sanction-status
    :sanction-status/green "Green"
    :sanction-status/yellow "Yellow"
    :sanction-status/red "Red"
    ""))

(defn get-screening-status-str
  [screening-status]
  (case screening-status
    :screening-status/active "Active"
    :screening-status/inactive "Inactive"
    ""))

(defn sanction-status-cell
  [sanction-status]
  [:div.d-flex.flex-column.align-items-start
   [:div.pl-2.pr-2.pt-1.pb-1
    {:style {:width         "60px"
             :background    (case sanction-status
                              :sanction-status/green "#8ABF4C"
                              :sanction-status/yellow "#F5BD3E"
                              :sanction-status/red "#E86546"
                              "")
             :color         "white"
             :border-radius "5px"
             :text-align    "center"}}
    (get-sanction-str sanction-status)]])

(defn row-style
  [screening-status]
  ^{:combinators {[:> :td] {:background (case screening-status
                                          :screening-status/inactive "#fff4ab !important"
                                          "")}}}
  {})

(defn open-notification!
  [_type title msg]
  (modals/open-modal!
   notification-modal/modal-key
   {:title          title
    :body-text      msg
    :ok-button-text (loc/tr :continue)}))

(defn get-screening-profiles-for-kyc
  [kyc-profile-id]
  (sp-actions/*->get-screening-profiles-for-kyc-profile
   {:kyc-profile-id kyc-profile-id}
   (fn [response]
     (let [screening-profiles (->> response
                                   :body
                                   :screening-profiles
                                   (map (fn [screening-profile]
                                          [(:db/id screening-profile)
                                           screening-profile]))
                                   (into {}))]
       (swap! app-db/entities merge screening-profiles)))
   (fn [err]
     (js/console.log "error " err))))

(defn render
  [{:keys [kyc-process answers-atom kyc-profile]}]
  (let [state (r/atom {:confirmation?   false
                       :submitting?     false
                       :submit-success? false
                       :submit-error?   false})
        {:keys [kyc-process/template db/id]} kyc-process]

    (fn []
      (let [kyc-process (-> (session/get-entity id)
                            (assoc :kyc-process/answers @answers-atom)
                            (assoc :common/containing-entity kyc-profile))
            screening-profiles @session/all-screening-profiles
            persons (sp-materialization/find-persons-from-kyc-process kyc-process)
            legal-entity (sp-materialization/find-legal-entity-from-kyc-process kyc-process)

            persons-with-screening-data
            (->> persons
                 (group-by (fn [{:keys [first-name last-name date-of-birth country]}]
                             (deduplicate-key-fn [first-name last-name date-of-birth country])))
                 (map (fn [[_ duplicates]]
                        (first duplicates)))
                 (map
                  (fn [person]
                    (let [existing-screening-profile
                          (find-matching-screening-profile :person
                                                           person
                                                           screening-profiles)
                          {:screening-profile/keys [pep-status
                                                    rca-status
                                                    sanction-status
                                                    screening-status
                                                    external-screening-id
                                                    uuid]}
                          existing-screening-profile]
                      (assoc person
                             :screening-profile-found? (boolean existing-screening-profile)
                             :pep-status pep-status
                             :rca-status rca-status
                             :sanction-status sanction-status
                             :screening-status screening-status
                             :external-screening-id external-screening-id
                             :uuid uuid)))))

            legal-entity-screening-profile (find-matching-screening-profile :company
                                                                            legal-entity
                                                                            screening-profiles)

            {:keys [screening-profile/screening-status
                    screening-profile/sanction-status
                    screening-profile/external-screening-id]} legal-entity-screening-profile
            {:keys [company-name business-id country]} legal-entity

            inactive-screening-profiles? (->> persons-with-screening-data
                                              (filter (fn [person]
                                                        (-> person
                                                            :screening-status
                                                            (= :screening-status/inactive))))
                                              (seq)
                                              (or (= (:screening-profile/screening-status
                                                      legal-entity-screening-profile)
                                                     :screening-status/inactive)))
            person-data-missing? (is-missing-data? persons)
            company-name-missing? (not company-name)
            missing-data? (or person-data-missing?
                              (and legal-entity
                                   company-name-missing?))
            {:keys [submitting? confirmation?]} @state]
        [:div.container
         [:div.card--kyc
          [:div.card-header
           [:h3 (loc/tr :screening)]]
          [:div.card-body
           [:div.row
            [:div.pl-3.pr-3.overflow-hidden
             (when (or (some #{template} kyc-templates/legal-entity-template-keys)
                       (some #{template} kyc-templates/tenant-template-keys)
                       (some #{template} kyc-templates/contract-template-keys))
               [:div.max-w-100.overflow-x-scroll.hide-scrollbar.mb-4
                [:h3.mb-2 (loc/tr :legal-entity)]
                [:table.zap-table.zap-table--striped.zap-table--has-footer.zap-table--width-auto
                 [:thead.zap-table__headline
                  [:tr.zap-table__row
                   [:th.zap-table__headline (loc/tr :screening-profile/company-name)]
                   [:th.zap-table__headline (loc/tr :screening-profile/business-id)]
                   [:th.zap-table__headline (loc/tr :screening-profile/country)]
                   [:th.zap-table__headline
                    {:class "border-l border-solid border-base-light-border-paler"}
                    (loc/tr :screening-profile/screening-status)]
                   [:th.zap-table__headline (loc/tr :screening-profile/sanction-status)]
                   [:th.zap-table__headline (loc/tr :riskscreen-link)]]]
                 [:tbody
                  [:tr
                   [:td.zap-table__cell company-name]
                   [:td.zap-table__cell business-id]
                   [:td.zap-table__cell (country/get-label country)]
                   (if legal-entity-screening-profile
                     [:<>
                      [:td.zap-table__cell
                       {:style {:border-left "1px solid #D9D9DA"}}
                       (get-screening-status-str screening-status)]
                      [:td.zap-table__cell
                       [sanction-status-cell sanction-status]]
                      [:td.zap-table__cell
                       (when external-screening-id
                         [:a
                          {:href   (get-entity-risk-screen-url external-screening-id)
                           :target "_blank"}
                          (loc/tr :view-in-riskscreen)])]]
                     [:td.zap-table__cell
                      {:col-span "3"
                       :style    {:border-left "1px solid #D9D9DA"
                                  :text-align  "center"}}
                      (loc/tr :no-screening-data-found)])]]]

                (when company-name-missing?
                  [:ul.mt-2.ml-1
                   {:style {:color "red"}}
                   [:li (loc/tr :screening-submit-error-legal-entity-data-missing)]])])

             [:div.max-w-100.overflow-x-scroll.hide-scrollbar
              [:h3.mb-2 (loc/tr :persons)]
              [:table.zap-table.zap-table--striped.zap-table--has-footer.zap-table--width-auto
               [:thead.zap-table__headline
                [:tr.zap-table__row
                 [:th.zap-table__headline (loc/tr :screening-profile/first-name)]
                 [:th.zap-table__headline (loc/tr :screening-profile/last-name)]
                 [:th.zap-table__headline (loc/tr :screening-profile/date-of-birth)]
                 [:th.zap-table__headline (loc/tr :screening-profile/country)]
                 [:th.zap-table__headline (loc/tr :declared-board-member)]
                 [:th.zap-table__headline (loc/tr :declared-pep)]
                 [:th.zap-table__headline (loc/tr :declared-ubo)]
                 [:th.zap-table__headline
                  {:class "border-l border-solid border-base-light-border-paler"}
                  (loc/tr :screening-profile/screening-status)]
                 [:th.zap-table__headline (loc/tr :screening-profile/sanction-status)]
                 [:th.zap-table__headline (loc/tr :screening-profile/pep-status)]
                 [:th.zap-table__headline (loc/tr :screening-profile/rca-status)]
                 [:th.zap-table__headline (loc/tr :riskscreen-link)]]]
               [:tbody
                (->> persons-with-screening-data
                     (map-indexed
                      (fn
                        [index
                         {:keys [screening-profile-found?
                                 first-name last-name
                                 country date-of-birth roles
                                 screening-status sanction-status pep-status
                                 rca-status external-screening-id]}]
                        (let [date-of-birth (when date-of-birth
                                              (fu/format-date-ts date-of-birth "." true))

                              country (when country
                                        (country/get-label country))

                              pep-status (when external-screening-id
                                           (if pep-status
                                             (loc/tr :true)
                                             (loc/tr :false)))

                              rca-status (when external-screening-id
                                           (if rca-status
                                             (loc/tr :true)
                                             (loc/tr :false)))

                              declared-board-member (if (some #{:board-member} roles)
                                                      (loc/tr :yes)
                                                      (loc/tr :no))
                              declared-pep (if (some #{:pep} roles)
                                             (loc/tr :yes)
                                             (loc/tr :no))
                              declared-ubo (if (some #{:ubo} roles)
                                             (loc/tr :yes)
                                             (loc/tr :no))]
                          ^{:key (str "screening-profile-" index)}
                          [:tr
                           [:td.zap-table__cell first-name]
                           [:td.zap-table__cell last-name]
                           [:td.zap-table__cell date-of-birth]
                           [:td.zap-table__cell country]
                           [:td.zap-table__cell declared-board-member]
                           [:td.zap-table__cell declared-pep]
                           [:td.zap-table__cell declared-ubo]
                           (if screening-profile-found?
                             [:<>
                              [:td.zap-table__cell
                               {:class "border-l border-solid border-base-light-border-paler"}
                               (get-screening-status-str screening-status)]
                              [:td.zap-table__cell
                               [sanction-status-cell sanction-status]]
                              [:td.zap-table__cell pep-status]
                              [:td.zap-table__cell rca-status]
                              [:td.zap-table__cell
                               (when external-screening-id
                                 [:a
                                  {:href   (get-entity-risk-screen-url external-screening-id)
                                   :target "_blank"}
                                  (loc/tr :view-in-riskscreen)])]]
                             [:td.zap-table__cell
                              {:col-span "6"}
                              (loc/tr :no-screening-data-found)])])))
                     (doall))]]]

             [:div
              [:div.mt-2.mb-3
               {:style {:color "red"}}
               (when person-data-missing?
                 (loc/tr :screening-submit-error-person-data-missing))]

              [:div.mt-3
               [:div
                (when inactive-screening-profiles?
                  [:div.mb-3.d-flex.align-items-center
                   [:div.mr-2
                    {:style {:background    "#fff4ab"
                             :width         "20px"
                             :height        "20px"
                             :border-radius "50%"}}]
                   (loc/tr :some-screening-profiles-inactive-please-re-submit)])

                (when-not missing-data?
                  [:div.d-flex.mb-3
                   [:input.mr-2
                    {:type      :checkbox
                     :disabled  submitting?
                     :id        :screening-confirmation
                     :checked   confirmation?
                     :on-change (fn [e]
                                  (let [checked? (.. e -target -checked)]
                                    (swap! state assoc :confirmation? checked?)))}]
                   [:label
                    {:for :screening-confirmation}
                    (loc/tr :screening-submit-confirmation)]])

                [button/primary-solid
                 {:disabled? (or (not confirmation?) missing-data?)
                  :on-click
                  (fn [e]
                    (fu/prevent-default-event! e)
                    (when-not submitting?
                      (swap! state assoc
                             :submitting? true
                             :submit-success? false
                             :confirmation? false)
                      (let [data {:source         :kyc
                                  :legal-entities (if legal-entity
                                                    [legal-entity]
                                                    [])
                                  :persons        persons
                                  :kyc-profile-id (:db/id kyc-profile)}]
                        (sp-actions/*->submit-data-for-screening!
                         data
                         (fn [_response]
                           (get-screening-profiles-for-kyc (:db/id kyc-profile))
                           (rf/dispatch [:open-notification
                                         {:title       (loc/tr :screening-submit-success)
                                          :type        :success
                                          :auto-close? true}])
                           (swap! state assoc
                                  :submitting? false
                                  :submit-success? true))
                         (fn [response]
                           (let [error-msg
                                 (case (:message response)
                                   :person-and-legal-entity-data-missing
                                   (loc/tr :screening-submit-error-person-and-legal-entity-data-missing)
                                   :person-data-missing
                                   (loc/tr :screening-submit-error-person-data-missing)
                                   :legal-entity-data-missing
                                   (loc/tr :screening-submit-error-legal-entity-data-missing)
                                   (loc/tr :screening-submit-error-unknown))]
                             (open-notification! :error
                                                 (loc/tr :screening-submit-error-title)
                                                 error-msg)
                             (swap! state assoc
                                    :submitting? false
                                    :submit-success? false)))))))}
                 (if submitting?
                   [:<>
                    [:i.fal.fa-sync.fa-pulse.mr-2]
                    (loc/tr :submitting)]
                   [:<>
                    (loc/tr :screening-submit-to-riskscreen)])]]]]]]]]]))))

(defn view
  [{:keys [kyc-process]}]
  (let [kyc-profile (get-containing-kyc-profile (:db/id kyc-process))]
    (r/create-class
     {:display-name           "screening-profile-table"
      :component-did-mount    (fn [_]
                                (get-screening-profiles-for-kyc (:db/id kyc-profile)))
      :component-will-unmount (fn [_]
                                (sp-events/clear-screening-profiles-from-app-db))
      :reagent-render         (fn [args]
                                (render (merge args
                                               {:kyc-profile kyc-profile})))})))
